// Copyright (c) 2021, 37.78 Tecnologia Ltda. All rights reserved.

import firebase from "firebase/app";

import "firebase/auth";
import "firebase/performance";
import errorHandler from "../util/errorHandler";
const initializeFirebase = () => {
  firebase.initializeApp(JSON.parse(process.env.REACT_APP_FIREBASE_CONFIG));
  firebase.performance();

  return firebase.auth();
};

const auth = initializeFirebase();

const setFirebasePersistenceLocal = async () =>
  auth.setPersistence(firebase.auth.Auth.Persistence.LOCAL);

const setFirebasePersistenceNone = async () =>
  auth.setPersistence(firebase.auth.Auth.Persistence.NONE);

const getFhirId = async (user) => {
  const idTokenResult = user
    ? await user.getIdTokenResult()
    : await auth.currentUser?.getIdTokenResult();
  return idTokenResult?.claims?.["https://hasura.io/jwt/claims"]?.[
    "x-hasura-user-id"
  ];
};

const isUserRegistered = async (user) => {
  if (!user) return false;
  const id = await getFhirId(user);
  return !!id;
};

const getToken = async () => auth.currentUser?.getIdToken();

const getEmail = () => auth.currentUser?.email;

const signUp = async ({ email, password }) => {
  try {
    await auth.createUserWithEmailAndPassword(email, password);
  } catch (e) {
    if (e.code !== "auth/email-already-in-use") {
      errorHandler.report(new Error(`${e.code}: ${e.message}`));
    }
    throw e;
  }
};

const verifyEmail = async (emailVerificationCode) => {
  try {
    await auth.applyActionCode(emailVerificationCode);

    if (auth.currentUser) {
      auth.currentUser.reload();
    }
  } catch (e) {
    errorHandler.report(new Error(`${e.code}: ${e.message}`));
    throw e;
  }
};

const fetchSignInMethodsForEmail = async (email) => {
  try {
    return await auth.fetchSignInMethodsForEmail(email);
  } catch (e) {
    if (e.code !== "auth/invalid-email") {
      errorHandler.report(new Error(`${e.code}: ${e.message}`));
    }
  }
};

const confirmPasswordReset = auth.confirmPasswordReset.bind(auth);
const logout = auth.signOut.bind(auth);
const onIdTokenChanged = auth.onIdTokenChanged.bind(auth);
const verifyPasswordResetCode = auth.verifyPasswordResetCode.bind(auth);

export {
  auth,
  confirmPasswordReset,
  fetchSignInMethodsForEmail,
  getEmail,
  getFhirId,
  getToken,
  initializeFirebase,
  isUserRegistered,
  logout,
  onIdTokenChanged,
  setFirebasePersistenceLocal,
  setFirebasePersistenceNone,
  signUp,
  verifyEmail,
  verifyPasswordResetCode,
};
